import { ReactElement, useCallback, useMemo } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useEventCallback } from '@mui/material';
import {
  createSearchParams,
  generatePath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import SipoPersonForm from 'shell/components/shared/sipo-person-form';
import PmiTitle from 'shell/components/shared/title';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { ForeignResidenceCrumbs } from 'subscription/constants/breadcrumb';
import { SubjectData } from 'shell/models/person';

const PersonSipoForeignResidencePage = (): ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const location = useLocation();

  const stateRequestApplicant = location.state as {
    accountHolderFiscalCode: string | undefined;
    preservedIntestatarioData?: unknown;
    preservedState?: {
      currentStep?: number;
      intestatarioData?: unknown;
      praticaKey?: {
        cdEnte: string;
        tipoPratica: string;
        idAnnoPratica: number;
        pgPratica: number;
      } | null;
    };
  };

  // id intestatrio se presente fare get perchè siamo in modifica
  const idIntestatario = queryParams.get('idIntestatario') || '';
  const idAnpr = queryParams.get('idAnpr') || '';
  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const isEdit = useMemo(() => {
    if ((idIntestatario && idIntestatario !== '0') || (idAnpr && idAnpr !== '0')) {
      return true;
    }

    return false;
  }, [idIntestatario, idAnpr]);

  const handleOnSuccess = useEventCallback(
    (newIdIntestatario?: string, state?: { subjectData?: SubjectData }) => {
      if (isEdit) {
        const navigationState = {
          ...stateRequestApplicant,
          ...stateRequestApplicant?.preservedState,
          preservedIntestatarioData: stateRequestApplicant?.preservedIntestatarioData,
        };

        navigate(
          {
            pathname: generatePath('/iscrizioni/residenza/estero'),
            search: `?${createSearchParams({
              idIntestatario: newIdIntestatario || idIntestatario || '',
              idAnpr: idAnpr || '',
              codiceFiscale: fiscalCode,
            })}`,
          },
          {
            state: navigationState,
          }
        );

        return;
      }

      navigate(
        {
          pathname: generatePath('/iscrizioni/residenza/estero'),
          search: `?${createSearchParams({
            idIntestatario: newIdIntestatario || idIntestatario || '',
            idAnpr: idAnpr || '',
            codiceFiscale: fiscalCode,
          })}`,
        },
        {
          state: { ...stateRequestApplicant, ...state },
        }
      );
    }
  );

  const handleExit = useCallback(() => {
    navigate('/iscrizioni/residenza/estero', {
      state: {
        ...stateRequestApplicant,
        ...stateRequestApplicant?.preservedState,
      },
    });
  }, [navigate, stateRequestApplicant]);

  return (
    <>
      <Breadcrumbs
        items={[
          ...ForeignResidenceCrumbs,
          {
            name: isEdit ? 'Modifica persona' : 'Nuova persona',
          },
        ]}
      />
      <PmiTitle title={isEdit ? 'Modifica persona' : 'Nuova persona'} />
      <SipoPersonForm
        onExit={handleExit}
        onSuccess={handleOnSuccess}
        urlGetData={
          idIntestatario && idIntestatario !== '0'
            ? `${__PERSON_API_URL__}/non-residenti/sipo/soggetto/${idIntestatario}`
            : `${__PERSON_NEW_API_URL__}/persona`
        }
        fiscalCode={fiscalCode}
        urlRegistration={`${__PERSON_API_URL__}/non-residenti/registrazione-aire`}
        urlUpdate={`${__PERSON_API_URL__}/non-residenti/modifica`}
      />
    </>
  );
};

PersonSipoForeignResidencePage.displayName = 'Person Sipo foreign residence Edit Page component';

export default PersonSipoForeignResidencePage;
